import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supportApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import {
  ClientPortalTicketAttachment,
  SupportStaffUser,
  SupportTicketDetail,
  SupportTicketMessage,
  SupportTicketSummary,
} from '../types';
import './Pages.css';

type AttachmentPreviewKind = 'image' | 'pdf' | 'text' | 'unsupported';

const ViewIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C3.9 7.9 7.5 5 12 5C16.5 5 20.1 7.9 22 12C20.1 16.1 16.5 19 12 19C7.5 19 3.9 16.1 2 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatFileSize(size: number): string {
  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} Ko`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function getStatusClassName(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized === 'traite') {
    return 'done';
  }
  if (normalized === 'archive') {
    return 'archived';
  }
  return 'progress';
}

function getTicketMessageAuthorLabel(
  ticket: SupportTicketDetail,
  message: Pick<SupportTicketMessage, 'sender_type' | 'staff_name'>
): string {
  if (message.sender_type === 'client') {
    return ticket.client_name;
  }

  return message.staff_name || 'Equipe support';
}

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'En cours', label: 'En cours' },
  { value: 'Traite', label: 'Traite' },
  { value: 'Archive', label: 'Archive' },
];

const TICKETS_PAGE_SIZE = 10;

function getAttachmentPreviewKind(
  attachment: ClientPortalTicketAttachment
): AttachmentPreviewKind {
  const contentType = (attachment.content_type || '').toLowerCase();
  const fileName = attachment.file_name.toLowerCase();

  if (contentType.startsWith('image/')) {
    return 'image';
  }

  if (contentType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return 'pdf';
  }

  if (
    contentType.startsWith('text/') ||
    contentType.includes('json') ||
    contentType.includes('xml') ||
    contentType.includes('csv') ||
    /\.(txt|log|csv|json|xml|ini|cfg|md)$/i.test(fileName)
  ) {
    return 'text';
  }

  return 'unsupported';
}

const SupportTickets: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();

  const isAdmin = hasPermission('admin', 'access');
  const canUpdateTickets = Boolean(user);
  const canViewTickets = Boolean(user);
  const canAssignTickets = isAdmin;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedStaffFilter, setAssignedStaffFilter] = useState('all');
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [staffUsers, setStaffUsers] = useState<SupportStaffUser[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetail | null>(null);
  const [assignmentDraft, setAssignmentDraft] = useState('0');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingStaffUsers, setLoadingStaffUsers] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
  const [previewAttachment, setPreviewAttachment] =
    useState<ClientPortalTicketAttachment | null>(null);
  const [previewKind, setPreviewKind] = useState<AttachmentPreviewKind>('unsupported');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);

  const loadTickets = useCallback(
    async (preferredTicketId?: number | null) => {
      setLoadingTickets(true);
      try {
        const params: { search?: string; status?: string; assigned_staff_id?: string } = {};
        if (search.trim()) {
          params.search = search.trim();
        }
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (canAssignTickets && assignedStaffFilter !== 'all') {
          params.assigned_staff_id = assignedStaffFilter;
        }

        const response = await supportApi.getTickets(params);
        const nextTickets = Array.isArray(response.data) ? response.data : [];
        setTickets(nextTickets);
        setSelectedTicketId((currentId) => {
          const targetId = preferredTicketId ?? currentId;
          if (!targetId) {
            return null;
          }
          return nextTickets.some((ticket) => ticket.id === targetId) ? targetId : null;
        });
      } catch (err: any) {
        showToast(err.response?.data?.error || 'Chargement des tickets impossible.', 'error');
      } finally {
        setLoadingTickets(false);
      }
    },
    [assignedStaffFilter, canAssignTickets, search, showToast, statusFilter]
  );

  const loadTicketDetail = useCallback(
    async (ticketId: number | null) => {
      if (!ticketId) {
        setSelectedTicket(null);
        return;
      }

      setLoadingDetail(true);
      try {
        const response = await supportApi.getTicketById(ticketId);
        const detail = response.data as SupportTicketDetail;
        setSelectedTicket(detail);
      } catch (err: any) {
        setSelectedTicket(null);
        showToast(err.response?.data?.error || 'Chargement du ticket impossible.', 'error');
      } finally {
        setLoadingDetail(false);
      }
    },
    [showToast]
  );

  const loadStaffUsers = useCallback(async () => {
    if (!canAssignTickets) {
      setStaffUsers([]);
      return;
    }

    setLoadingStaffUsers(true);
    try {
      const response = await supportApi.getStaffUsers();
      setStaffUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Chargement du staff impossible.', 'error');
    } finally {
      setLoadingStaffUsers(false);
    }
  }, [canAssignTickets, showToast]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    void loadStaffUsers();
  }, [loadStaffUsers]);

  useEffect(() => {
    void loadTicketDetail(selectedTicketId);
  }, [loadTicketDetail, selectedTicketId]);

  useEffect(() => {
    setAssignmentDraft(
      selectedTicket?.assigned_staff_id ? String(selectedTicket.assigned_staff_id) : '0'
    );
  }, [selectedTicket]);

  useEffect(() => {
    setReplyText('');
  }, [selectedTicketId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, assignedStaffFilter, canAssignTickets]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const staffOptions = useMemo(() => {
    if (!selectedTicket?.assigned_staff_id) {
      return staffUsers;
    }

    if (staffUsers.some((staffUser) => staffUser.id === selectedTicket.assigned_staff_id)) {
      return staffUsers;
    }

    return [
      {
        id: selectedTicket.assigned_staff_id,
        user_name: selectedTicket.assigned_staff_name || `Staff #${selectedTicket.assigned_staff_id}`,
        role_name: 'Hors liste',
      },
      ...staffUsers,
    ];
  }, [selectedTicket, staffUsers]);

  const counts = useMemo(() => {
    return tickets.reduce(
      (acc, ticket) => {
        if (ticket.status === 'Traite') {
          acc.done += 1;
        } else if (ticket.status === 'Archive') {
          acc.archived += 1;
        } else {
          acc.progress += 1;
        }
        return acc;
      },
      { progress: 0, done: 0, archived: 0 }
    );
  }, [tickets]);

  const pageCount = useMemo(() => {
    if (tickets.length === 0) {
      return 1;
    }

    return Math.ceil(tickets.length / TICKETS_PAGE_SIZE);
  }, [tickets.length]);

  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * TICKETS_PAGE_SIZE;
    return tickets.slice(startIndex, startIndex + TICKETS_PAGE_SIZE);
  }, [currentPage, tickets]);

  const visiblePages = useMemo(() => {
    const windowSize = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(pageCount, startPage + windowSize - 1);
    startPage = Math.max(1, endPage - windowSize + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [currentPage, pageCount]);

  const canUpdateSelectedTicket = Boolean(
    selectedTicket &&
      (isAdmin || (canUpdateTickets && selectedTicket.assigned_staff_id === user?.id))
  );

  const selectedTicketConversation = useMemo(() => {
    if (!selectedTicket) {
      return [];
    }

    const initialMessage: SupportTicketMessage = {
      id: 0,
      sender_type: 'client',
      staff_id: null,
      staff_name: null,
      message_text: selectedTicket.message_text,
      created_at: selectedTicket.created_at,
      updated_at: selectedTicket.created_at,
    };

    return [initialMessage, ...(selectedTicket.messages || [])];
  }, [selectedTicket]);

  useEffect(() => {
    setCurrentPage((previousPage) => Math.min(previousPage, pageCount));
  }, [pageCount]);

  const handleSaveAssignment = async () => {
    if (!selectedTicket) {
      return;
    }

    setSavingAssignment(true);
    try {
      const nextAssignedId =
        assignmentDraft !== '0' ? Number.parseInt(assignmentDraft, 10) : null;
      await supportApi.assignTicket(selectedTicket.id, nextAssignedId);
      showToast('Affectation mise a jour.', 'success');
      await Promise.all([
        loadTickets(selectedTicket.id),
        loadTicketDetail(selectedTicket.id),
      ]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Affectation impossible.', 'error');
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedTicket) {
      return;
    }

    setUpdatingStatus(status);
    try {
      await supportApi.updateTicketStatus(selectedTicket.id, status);
      showToast('Statut du ticket mis a jour.', 'success');
      await Promise.all([
        loadTickets(selectedTicket.id),
        loadTicketDetail(selectedTicket.id),
      ]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Mise a jour du statut impossible.', 'error');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleReplyToTicket = async () => {
    if (!selectedTicket) {
      return;
    }

    if (!replyText.trim()) {
      showToast('Le texte de la reponse est obligatoire.', 'warning');
      return;
    }

    setSendingReply(true);
    try {
      const response = await supportApi.replyToTicket(selectedTicket.id, replyText.trim());
      setReplyText('');
      if (response.data.warning) {
        showToast(response.data.warning, 'warning');
      } else {
        showToast('Reponse ajoutee au ticket.', 'success');
      }

      await Promise.all([loadTickets(selectedTicket.id), loadTicketDetail(selectedTicket.id)]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Envoi de la reponse impossible.', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDownloadAttachment = async (attachment: ClientPortalTicketAttachment) => {
    if (!selectedTicket) {
      return;
    }

    setDownloadingAttachmentId(attachment.id);
    try {
      const response = await supportApi.downloadAttachment(selectedTicket.id, attachment.id);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Telechargement impossible.', 'error');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    setPreviewKind('unsupported');
    setPreviewText('');
    setPreviewUrl(null);
    setPreviewLoadingId(null);
  };

  const closeTicketDetail = () => {
    closePreview();
    setSelectedTicket(null);
    setSelectedTicketId(null);
  };

  const handlePreviewAttachment = async (attachment: ClientPortalTicketAttachment) => {
    if (!selectedTicket) {
      return;
    }

    const nextPreviewKind = getAttachmentPreviewKind(attachment);
    if (nextPreviewKind === 'unsupported') {
      showToast('Apercu disponible uniquement pour image, PDF et texte.', 'error');
      return;
    }

    setPreviewAttachment(attachment);
    setPreviewKind(nextPreviewKind);
    setPreviewText('');
    setPreviewUrl(null);
    setPreviewLoadingId(attachment.id);

    try {
      const response = await supportApi.downloadAttachment(selectedTicket.id, attachment.id);
      const blob = response.data as Blob;

      if (nextPreviewKind === 'text') {
        const textContent = await blob.text();
        setPreviewText(textContent);
      } else {
        setPreviewUrl(window.URL.createObjectURL(blob));
      }
    } catch (err: any) {
      closePreview();
      showToast(err.response?.data?.error || 'Apercu impossible.', 'error');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  if (!canViewTickets) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Support Tickets</h1>
      </div>

      <div className="support-stats">
        <div className="support-stat-card">
          <strong>{tickets.length}</strong>
          <span>Tickets affiches</span>
        </div>
        <div className="support-stat-card">
          <strong>{counts.progress}</strong>
          <span>En cours</span>
        </div>
        <div className="support-stat-card">
          <strong>{counts.done}</strong>
          <span>Traites</span>
        </div>
        <div className="support-stat-card">
          <strong>{counts.archived}</strong>
          <span>Archives</span>
        </div>
      </div>

      <div className="filters">
        <input
          className="search-input"
          type="text"
          placeholder="Recherche client, sujet, email, telephone"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {canAssignTickets && (
          <select
            value={assignedStaffFilter}
            onChange={(event) => setAssignedStaffFilter(event.target.value)}
            disabled={loadingStaffUsers}
          >
            <option value="all">Tous les tickets</option>
            <option value="0">Non affectes</option>
            {staffUsers.map((staffUser) => (
              <option key={staffUser.id} value={String(staffUser.id)}>
                {staffUser.user_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {!canAssignTickets && (
        <div className="info-message">
          Vous voyez uniquement les tickets affectes a votre compte staff.
        </div>
      )}

      {selectedTicketId ? (
        <div className="support-detail support-detail-page">
          <div className="support-detail-toolbar">
            <button type="button" className="btn btn-secondary support-back-button" onClick={closeTicketDetail}>
              Retour vers la liste
            </button>
          </div>

          {loadingDetail ? (
            <div className="loading">Chargement du ticket...</div>
          ) : !selectedTicket ? (
            <div className="support-empty-state">Detail du ticket introuvable.</div>
          ) : (
            <>
              <div className="support-detail-header">
                <div>
                  <h2>Ticket #{selectedTicket.id}</h2>
                  <p>{selectedTicket.subject}</p>
                </div>
                <span className={`support-status-badge ${getStatusClassName(selectedTicket.status)}`}>
                  {selectedTicket.status}
                </span>
              </div>

              <div className="support-meta-grid">
                <div><strong>Client</strong><span>{selectedTicket.client_name}</span></div>
                <div><strong>Telephone</strong><span>{selectedTicket.telephone || '--'}</span></div>
                <div><strong>Email</strong><span>{selectedTicket.client_email || '--'}</span></div>
                <div><strong>Depose le</strong><span>{formatDateTime(selectedTicket.created_at)}</span></div>
                <div><strong>Affecte a</strong><span>{selectedTicket.assigned_staff_name || 'Non affecte'}</span></div>
                <div><strong>Assigne par</strong><span>{selectedTicket.assigned_by_staff_name || '--'}</span></div>
                <div><strong>Traite par</strong><span>{selectedTicket.treated_by_staff_name || '--'}</span></div>
                <div><strong>Date traitement</strong><span>{formatDateTime(selectedTicket.treated_at)}</span></div>
              </div>

              {canAssignTickets && (
                <div className="support-action-card">
                  <h3>Affectation</h3>
                  <div className="support-inline-form">
                    <select
                      value={assignmentDraft}
                      onChange={(event) => setAssignmentDraft(event.target.value)}
                      disabled={savingAssignment || loadingStaffUsers}
                    >
                      <option value="0">Non affecte</option>
                      {staffOptions.map((staffUser) => (
                        <option key={staffUser.id} value={String(staffUser.id)}>
                          {staffUser.user_name}
                          {staffUser.role_name ? ` (${staffUser.role_name})` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveAssignment}
                      disabled={savingAssignment || assignmentDraft === (selectedTicket.assigned_staff_id ? String(selectedTicket.assigned_staff_id) : '0')}
                    >
                      {savingAssignment ? 'En cours...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              )}

              <div className="support-action-card">
                <h3>Statut</h3>
                <div className="support-status-actions">
                  {['En cours', 'Traite', 'Archive'].map((status) => (
                    <button
                      key={status}
                      className={`btn ${selectedTicket.status === status ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleUpdateStatus(status)}
                      disabled={!canUpdateSelectedTicket || updatingStatus === status}
                    >
                      {updatingStatus === status ? 'Mise a jour...' : status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="support-action-card">
                <h3>Conversation du ticket</h3>
                <div className="support-thread-list">
                  {selectedTicketConversation.map((message, index) => (
                    <div
                      key={`${message.sender_type}-${message.id}-${index}`}
                      className={`support-thread-item ${
                        message.sender_type === 'staff' ? 'staff' : 'client'
                      }`}
                    >
                      <div className="support-thread-meta">
                        <strong>{getTicketMessageAuthorLabel(selectedTicket, message)}</strong>
                        <span>{formatDateTime(message.created_at)}</span>
                      </div>
                      <div className="support-message-box">{message.message_text}</div>
                    </div>
                  ))}
                </div>

                {canUpdateSelectedTicket && (
                  <div className="support-reply-composer">
                    <div className="form-group">
                      <label>Reponse au client</label>
                      <textarea
                        rows={5}
                        value={replyText}
                        onChange={(event) => setReplyText(event.target.value)}
                        placeholder="Saisissez votre reponse au client."
                      />
                    </div>
                    <div className="support-reply-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleReplyToTicket}
                        disabled={sendingReply || !replyText.trim()}
                      >
                        {sendingReply ? 'Envoi...' : 'Envoyer la reponse'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="support-action-card">
                <h3>Pieces jointes</h3>
                {!selectedTicket.attachments || selectedTicket.attachments.length === 0 ? (
                  <div className="support-empty-state compact">Aucune piece jointe.</div>
                ) : (
                  <div className="support-attachment-list">
                    {selectedTicket.attachments.map((attachment) => (
                      <div key={attachment.id} className="support-attachment-item">
                        <div className="support-attachment-content">
                          <span>{attachment.file_name}</span>
                          <small>
                            {formatFileSize(attachment.file_size)} |{' '}
                            {formatDateTime(attachment.created_at)}
                          </small>
                        </div>
                        <div className="support-attachment-actions">
                          {getAttachmentPreviewKind(attachment) !== 'unsupported' && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handlePreviewAttachment(attachment)}
                              disabled={previewLoadingId === attachment.id}
                            >
                              {previewLoadingId === attachment.id ? 'Ouverture...' : 'Apercu'}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleDownloadAttachment(attachment)}
                            disabled={downloadingAttachmentId === attachment.id}
                          >
                            {downloadingAttachmentId === attachment.id
                              ? 'Telechargement...'
                              : 'Telecharger'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="support-layout">
          <div className="support-list">
            <div className="support-list-card">
              <div className="support-list-header">
                <div>
                  <h2>Liste des tickets</h2>
                  <p>
                    {tickets.length === 0
                      ? 'Aucun ticket'
                      : `${(currentPage - 1) * TICKETS_PAGE_SIZE + 1}-${Math.min(
                          currentPage * TICKETS_PAGE_SIZE,
                          tickets.length
                        )} sur ${tickets.length}`}
                  </p>
                </div>
                {pageCount > 1 && (
                  <div className="support-pagination">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Prec.
                    </button>
                    {visiblePages.map((page) => (
                      <button
                        key={page}
                        type="button"
                        className={`btn ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                      disabled={currentPage === pageCount}
                    >
                      Suiv.
                    </button>
                  </div>
                )}
              </div>

              {loadingTickets ? (
                <div className="loading">Chargement des tickets...</div>
              ) : tickets.length === 0 ? (
                <div className="support-empty-state">Aucun ticket ne correspond aux filtres.</div>
              ) : (
                <div className="table-container support-table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Client</th>
                        <th>Sujet</th>
                        <th>Statut</th>
                        <th>Affecte a</th>
                        <th>Pieces</th>
                        <th>Depot</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td>{ticket.id}</td>
                          <td>{ticket.client_name}</td>
                          <td>{ticket.subject}</td>
                          <td>
                            <span className={`support-status-badge ${getStatusClassName(ticket.status)}`}>
                              {ticket.status}
                            </span>
                          </td>
                          <td>{ticket.assigned_staff_name || 'Non affecte'}</td>
                          <td>{ticket.attachment_count}</td>
                          <td>{formatDateTime(ticket.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => setSelectedTicketId(ticket.id)}
                                title="Voir le detail du ticket"
                                aria-label={`Voir le detail du ticket ${ticket.id}`}
                              >
                                <ViewIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewAttachment && (
        <div className="modal-overlay" onClick={closePreview}>
          <div
            className="modal modal-wide support-preview-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>Apercu: {previewAttachment.file_name}</h2>
            <div className="support-preview-meta">
              {formatFileSize(previewAttachment.file_size)} |{' '}
              {formatDateTime(previewAttachment.created_at)}
            </div>

            {previewLoadingId === previewAttachment.id ? (
              <div className="loading">Chargement de la piece jointe...</div>
            ) : previewKind === 'image' && previewUrl ? (
              <div className="support-preview-frame">
                <img src={previewUrl} alt={previewAttachment.file_name} />
              </div>
            ) : previewKind === 'pdf' && previewUrl ? (
              <div className="support-preview-frame">
                <iframe src={previewUrl} title={previewAttachment.file_name} />
              </div>
            ) : previewKind === 'text' ? (
              <pre className="support-preview-text">{previewText || 'Fichier vide.'}</pre>
            ) : (
              <div className="support-empty-state compact">
                Apercu indisponible pour ce format.
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closePreview}>
                Fermer
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleDownloadAttachment(previewAttachment)}
                disabled={downloadingAttachmentId === previewAttachment.id}
              >
                {downloadingAttachmentId === previewAttachment.id
                  ? 'Telechargement...'
                  : 'Telecharger'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
